import { useEffect, useState } from "react";
import { KeyRound, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, BACKEND_URL } from "../lib/api";
import { useSeo } from "../lib/seo";
import { formatDate } from "../lib/format";

export default function Tokens() {
  useSeo({ title: "API Tokens — Admin", robots: "noindex,nofollow" });
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const load = () => api.get("/admin/tokens").then((r) => setTokens(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/tokens", { name }); setFresh(data); setName(""); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const revoke = async (t) => { if (!window.confirm(`Revoke "${t.name}"?`)) return; await api.delete(`/admin/tokens/${t.id}`); toast.success("Revoked"); load(); };

  return (
    <div data-testid="admin-tokens">
      <p className="eyebrow">Automation</p>
      <h1 className="mt-1 font-serif text-3xl font-semibold">API Tokens</h1>
      <p className="mt-2 max-w-2xl text-sm text-ab-muted">Tokens let external tools (n8n, scripts) create and update articles via the Content API. Send <code className="rounded bg-ab-soft px-1 font-mono text-xs">Authorization: Bearer ab_…</code> to <code className="rounded bg-ab-soft px-1 font-mono text-xs">POST {BACKEND_URL}/api/articles</code>.</p>

      <form onSubmit={create} className="card-ab mt-6 flex flex-col gap-3 p-5 sm:flex-row" data-testid="token-create-form">
        <input required minLength={2} placeholder="Token name, e.g. n8n production" value={name} onChange={(e) => setName(e.target.value)} className="input-ab flex-1" data-testid="token-name-input" />
        <button type="submit" className="btn-primary" data-testid="token-create-button"><KeyRound size={15} /> Create token</button>
      </form>

      {fresh && (
        <div className="card-ab mt-4 border-ab-accent bg-ab-accent-soft p-5" data-testid="token-fresh">
          <p className="text-sm font-semibold text-[#1D5239]">Copy this token now. It will not be shown again.</p>
          <div className="mt-2 flex items-center gap-2"><code className="flex-1 overflow-x-auto rounded-lg bg-white px-3 py-2 font-mono text-xs" data-testid="token-fresh-value">{fresh.token}</code><button type="button" onClick={() => { navigator.clipboard?.writeText(fresh.token); toast.success("Copied"); }} className="btn-secondary !px-3 !py-2" aria-label="Copy token"><Copy size={14} /></button></div>
        </div>
      )}

      <div className="card-ab mt-6 overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-ab-soft text-left text-xs uppercase tracking-wider text-ab-muted"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Prefix</th><th className="px-4 py-3">Created</th><th className="px-4 py-3">Last used</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody className="divide-y divide-ab-border">
            {tokens.map((t) => <tr key={t.id} data-testid={`token-row-${t.id}`}><td className="px-4 py-3 font-medium">{t.name}</td><td className="px-4 py-3 font-mono text-xs">{t.prefix}…</td><td className="px-4 py-3 text-ab-muted">{formatDate(t.created_at)}</td><td className="px-4 py-3 text-ab-muted">{t.last_used_at ? formatDate(t.last_used_at) : "Never"}</td><td className="px-4 py-3">{t.revoked ? <span className="text-red-600">Revoked</span> : <span className="text-emerald-700">Active</span>}</td><td className="px-4 py-3 text-right">{!t.revoked && <button type="button" onClick={() => revoke(t)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label="Revoke" data-testid={`token-revoke-${t.id}`}><Trash2 size={15} /></button>}</td></tr>)}
            {tokens.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-ab-muted">No tokens yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card-ab mt-8 p-6 text-sm">
        <h2 className="font-semibold">Example payload</h2>
        <pre className="mt-3 overflow-x-auto rounded-xl bg-[#1E1E1E] p-4 font-mono text-xs leading-relaxed text-white">{`POST ${BACKEND_URL}/api/articles
Authorization: Bearer ab_xxxxxxxx
Content-Type: application/json

{
  "title": "Best Time to Buy a Mattress",
  "slug": "mattress",
  "category": "best-time-to-buy",
  "tags": ["mattress", "bedroom"],
  "excerpt": "Mattress pricing follows a predictable rhythm...",
  "content": "<h2>Why This Matters</h2><p>...</p>",
  "seo_title": "Best Time to Buy a Mattress",
  "seo_description": "...",
  "status": "draft"
}`}</pre>
      </div>
    </div>
  );
}

export function Subscribers() {
  useSeo({ title: "Subscribers — Admin", robots: "noindex,nofollow" });
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/subscribers").then((r) => setData(r.data)); }, []);
  return (
    <div data-testid="admin-subscribers">
      <p className="eyebrow">Newsletter</p>
      <h1 className="mt-1 font-serif text-3xl font-semibold">Subscribers {data && <span className="text-lg text-ab-muted">({data.total})</span>}</h1>
      <div className="card-ab mt-6 overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-ab-soft text-left text-xs uppercase tracking-wider text-ab-muted"><tr><th className="px-4 py-3">Email</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Joined</th></tr></thead>
          <tbody className="divide-y divide-ab-border">{data?.items.map((s) => <tr key={s.id}><td className="px-4 py-3 font-medium">{s.email}</td><td className="px-4 py-3 text-ab-muted">{s.source}</td><td className="px-4 py-3 text-ab-muted">{formatDate(s.created_at)}</td></tr>)}{data && data.items.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-ab-muted">No subscribers yet.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}
